import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { paths } from '@/lib/apiPaths';
import { qkReportThemes } from '@/lib/queryKeys';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import { useTheme } from '@/contexts/ThemeContext';

// Import report components
import LocalizationCoverageReport from '@/features/reports/LocalizationCoverageReport';
import ChangeHistoryReport from '@/features/reports/ChangeHistoryReport';
import AttributeInsightsReport from '@/features/reports/AttributeInsightsReport';
import ReadinessReport from '@/features/reports/ReadinessReport';
import EnrichmentVelocityReport from '@/features/reports/EnrichmentVelocityReport';

// Import types

// Enterprise Analytics Hooks
import { useCompletenessReport } from '@/hooks/useAnalyticsReports';

// Dashboard summary service for completeness integration
import { config } from '@/config/config';

// Import the translation hook
import { useReportspageTranslations } from '@/hooks/useReportspageTranslations';

// Enterprise analytics integration - no longer need legacy dashboard summary

interface Theme {
  slug: string;
  name: string;
  description: string;
}

// Define attribute and category data types
interface AttributeData {
  name: string;
  completed: number;
  total: number;
}

interface CategoryData {
  name: string;
  value: number;
}

interface Category {
  id: number;
  name: string;
  parent: number | null;
  children?: Category[];
}

// Get chart colors from config
const COLORS = config.reports.display.chartColors;

const CompletenessReport: React.FC = () => {
  const [showAllAttributes, setShowAllAttributes] = useState(false);

  // Get translations and theme
  const i18n = useReportspageTranslations();
  const { actualTheme } = useTheme();

  // Use enterprise analytics hook for completeness data
  const { data: apiData, isLoading, error } = useCompletenessReport();

  // Fetch categories to resolve IDs to names
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () =>
      axiosInstance
        .get<Category[]>(paths.categories.root() + '?as_tree=false')
        .then(res => res.data),
    staleTime: config.reports.staleTimes.themes,
  });

  // Theme-aware colors
  const barColor = actualTheme === 'dark' ? '#60a5fa' : COLORS[0]; // Light blue for dark mode
  const categoryBarColor = actualTheme === 'dark' ? '#34d399' : COLORS[1]; // Light green for dark mode
  const labelColor = actualTheme === 'dark' ? '#e5e7eb' : '#6b7280'; // Light gray for dark mode

  // Process the data to ensure it matches the expected structure
  const data = useMemo(() => {
    // ZERO-TOLERANCE MANDATE: NO FALLBACK DATA
    // If no data from analytics API, return null to show error state
    if (!apiData) return null;

    // Use the analytics endpoint's calculation directly
    const overall = typeof apiData.overall === 'number' ? apiData.overall : 0;

    // Ensure all expected properties exist - NO FALLBACKS, LIVE DATA ONLY
    return {
      overall,
      byAttribute:
        Array.isArray(apiData.byAttribute) && apiData.byAttribute.length > 0
          ? apiData.byAttribute.map((attr: AttributeData) => ({
              name: attr.name || i18n.statusLabels.unknown,
              completed: typeof attr.completed === 'number' ? attr.completed : 0,
              total: typeof attr.total === 'number' ? attr.total : 0,
            }))
          : [], // Empty array if no live data
      byCategory:
        Array.isArray(apiData.byCategory) && apiData.byCategory.length > 0
          ? apiData.byCategory.map((cat: CategoryData) => ({
              name: cat.name || i18n.statusLabels.unknown,
              value: typeof cat.value === 'number' ? cat.value : 0,
            }))
          : [], // Empty array if no live data
    };
  }, [apiData, i18n.statusLabels.unknown]);

  // Create a lookup map for category IDs to names
  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();

    if (categories) {
      categories.forEach(category => {
        map.set(category.id.toString(), category.name);
      });
    }

    return map;
  }, [categories]);

  // Process the data for displaying categories with proper names
  const categoryData = useMemo(() => {
    if (!data || !data.byCategory || data.byCategory.length === 0) return [];

    return data.byCategory.map((cat: CategoryData) => {
      // If the name is a number (likely a category ID), try to replace it with the actual name
      const name = cat.name;
      let displayName = name;

      // Check if it's a numeric string (likely a category ID)
      if (/^\d+$/.test(name) && categoryMap.has(name)) {
        displayName = categoryMap.get(name) || i18n.statusLabels.unknownCategory;
      } else if (name === 'Uncategorized') {
        displayName = i18n.statusLabels.uncategorized;
      }

      return {
        ...cat,
        name: displayName,
        originalId: name,
      };
    });
  }, [data, categoryMap, i18n.statusLabels]);

  // Calculate completion counts for the stats
  const completionStats = useMemo(() => {
    if (!data)
      return {
        totalCount: 0,
        completedCount: 0,
        missingCount: 0,
        percent: 0,
        mostCompleted: [],
        mostMissing: [],
      };

    // Get total counts from byAttribute data
    const totalCount = data.byAttribute.reduce(
      (sum: number, attr: AttributeData) => sum + (attr.total || 0),
      0
    );
    const completedCount = data.byAttribute.reduce(
      (sum: number, attr: AttributeData) => sum + (attr.completed || 0),
      0
    );
    const missingCount = totalCount - completedCount;
    const percent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    // Find top 3 most completed and most missing attributes
    const attributesWithPercentage = data.byAttribute.map((attr: AttributeData) => {
      const total = attr.total || 0;
      const completed = attr.completed || 0;
      const completionRate = total > 0 ? completed / total : 0;
      return {
        name: attr.name || i18n.statusLabels.unknown,
        completionRate,
        missingRate: 1 - completionRate,
        completed,
        missing: total - completed,
        total,
      };
    });

    const mostCompleted = attributesWithPercentage
      .filter(attr => attr.total > 0)
      .sort((a, b) => b.completionRate - a.completionRate)
      .slice(0, 3);

    const mostMissing = attributesWithPercentage
      .filter(attr => attr.missing > 0)
      .sort((a, b) => b.missingRate - a.missingRate)
      .slice(0, 3);

    return {
      totalCount,
      completedCount,
      missingCount,
      percent,
      mostCompleted,
      mostMissing,
    };
  }, [data, i18n.statusLabels.unknown]);

  // Prepare chart data for attributes
  const attributeChartData = useMemo(() => {
    if (!data) return [];

    const maxItems = showAllAttributes
      ? data.byAttribute.length
      : config.reports.display.defaultMaxItems;

    return data.byAttribute
      .slice(0, maxItems)
      .map((attr: AttributeData) => {
        const total = attr.total || 0;
        const completed = attr.completed || 0;
        const pct = total > 0 ? parseFloat(((completed / total) * 100).toFixed(1)) : 0;

        return {
          name: attr.name || i18n.statusLabels.unknown,
          completed,
          total,
          pct,
        };
      })
      .sort((a, b) => a.pct - b.pct); // Sort by completion percentage (lowest first)
  }, [data, showAllAttributes, i18n.statusLabels.unknown]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="mb-2 h-8 w-16" />
                <Skeleton className="h-3 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <p className="mb-2 text-red-600">{i18n.errors.completeness.failedToLoad}</p>
            <p className="text-sm text-gray-500">
              {error instanceof Error ? error.message : i18n.errors.completeness.unexpectedError}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ZERO-TOLERANCE MANDATE: If no live data available, show error state
  if (!data) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <p className="mb-2 text-red-600">{i18n.errors.completeness.noDataAvailable}</p>
            <p className="text-sm text-gray-500">{i18n.errors.completeness.contactAdmin}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle
              className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
            >
              {i18n.summaryCards.completeness.overallCompleteness}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold ${actualTheme === 'dark' ? 'text-foreground' : 'text-enterprise-900'}`}
            >
              {data.overall.toFixed(1)}%
            </div>
            <p
              className={`mt-1 text-xs ${actualTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
            >
              {completionStats.completedCount.toLocaleString()}{' '}
              {i18n.summaryCards.completeness.ofAttributes(completionStats.totalCount)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle
              className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
            >
              {i18n.summaryCards.completeness.completedAttributes}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {completionStats.completedCount.toLocaleString()}
            </div>
            <p
              className={`mt-1 text-xs ${actualTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
            >
              {completionStats.percent}% {i18n.summaryCards.completeness.completionRate}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle
              className={`text-sm font-medium ${actualTheme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}
            >
              {i18n.summaryCards.completeness.missingAttributes}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {completionStats.missingCount.toLocaleString()}
            </div>
            <p
              className={`mt-1 text-xs ${actualTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
            >
              {100 - completionStats.percent}% {i18n.summaryCards.completeness.missing}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Completeness by Attribute */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">
              {i18n.charts.completeness.completenessByAttribute}
            </CardTitle>
            <CardDescription>
              {i18n.charts.completeness.completenessByAttributeDescription}
            </CardDescription>
          </div>
          {data.byAttribute.length > config.reports.display.defaultMaxItems && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllAttributes(!showAllAttributes)}
            >
              {showAllAttributes
                ? i18n.buttons.completeness.showLess
                : i18n.buttons.completeness.showAll(data.byAttribute.length)}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div
            className="h-auto"
            style={{
              minHeight: `${config.reports.display.defaultChartHeight}px`,
            }}
          >
            {attributeChartData.length > 0 ? (
              <ResponsiveContainer
                width="100%"
                height={Math.max(
                  config.reports.display.defaultChartHeight,
                  attributeChartData.length * config.reports.display.attributeChartRowHeight
                )}
              >
                <BarChart
                  data={attributeChartData}
                  layout="vertical"
                  margin={{ top: 5, right: 50, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    unit="%"
                    tickCount={6}
                    tick={{ fill: actualTheme === 'dark' ? '#e5e7eb' : '#6b7280', fontSize: 12 }}
                  />
                  <YAxis
                    dataKey="name"
                    type="category"
                    width={120}
                    tick={{ fill: actualTheme === 'dark' ? '#e5e7eb' : '#6b7280', fontSize: 12 }}
                    tickFormatter={value =>
                      value.length > 25 ? `${value.substring(0, 22)}...` : value
                    }
                  />
                  <Tooltip
                    formatter={value => {
                      return [
                        `${Number(value).toFixed(1)}% ${i18n.charts.completeness.complete}`,
                        '',
                      ];
                    }}
                    labelFormatter={name => {
                      const item = attributeChartData.find(i => i.name === name);
                      return i18n.getAttributeTooltip(name, item?.completed || 0, item?.total || 0);
                    }}
                    contentStyle={{
                      backgroundColor: actualTheme === 'dark' ? '#1f2937' : '#ffffff',
                      border: actualTheme === 'dark' ? '1px solid #374151' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      color: actualTheme === 'dark' ? '#f9fafb' : '#111827',
                    }}
                  />
                  <Bar dataKey="pct" fill={barColor} radius={[0, 4, 4, 0]} barSize={20}>
                    <LabelList
                      dataKey="pct"
                      position="right"
                      formatter={(value: number) => `${value.toFixed(1)}%`}
                      style={{ fill: labelColor, fontWeight: 500 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div
                className={`flex h-full items-center justify-center ${actualTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
              >
                {i18n.errors.completeness.noAttributeData}
              </div>
            )}
          </div>
          <div
            className={`mt-3 text-center text-sm ${actualTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
          >
            {attributeChartData.length > 0 &&
              !showAllAttributes &&
              data.byAttribute.length > config.reports.display.defaultMaxItems && (
                <p>
                  {i18n.charts.completeness.moreAttributesNotShown(
                    data.byAttribute.length - config.reports.display.defaultMaxItems
                  )}
                </p>
              )}
          </div>
        </CardContent>
      </Card>

      {/* Completeness by Category */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {i18n.charts.completeness.completenessByCategory}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px]">
            {categoryData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: actualTheme === 'dark' ? '#e5e7eb' : '#6b7280', fontSize: 12 }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    unit="%"
                    tick={{ fill: actualTheme === 'dark' ? '#e5e7eb' : '#6b7280', fontSize: 12 }}
                  />
                  <Tooltip
                    formatter={value => [`${value}%`, i18n.charts.completeness.completeness]}
                    contentStyle={{
                      backgroundColor: actualTheme === 'dark' ? '#1f2937' : '#ffffff',
                      border: actualTheme === 'dark' ? '1px solid #374151' : '1px solid #e5e7eb',
                      borderRadius: '6px',
                      color: actualTheme === 'dark' ? '#f9fafb' : '#111827',
                    }}
                  />
                  <Bar dataKey="value" fill={categoryBarColor} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div
                className={`flex h-full items-center justify-center ${actualTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
              >
                {i18n.errors.completeness.noCategoryData}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ReportsPage: React.FC = () => {
  // Get translations and theme
  const i18n = useReportspageTranslations();
  const { actualTheme } = useTheme();

  // Active report tab
  const [activeReport, setActiveReport] = useState<string>('completeness');

  // Fetch report themes from API
  const {
    data: _fetchedThemes = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: qkReportThemes(),
    queryFn: () => axiosInstance.get<Theme[]>(paths.reports.themes()).then(res => res.data),
    staleTime: config.reports.staleTimes.themes,
  });

  // Get report definitions from configuration with translations
  const REPORTS = useMemo(
    () =>
      config.reports.definitions.map(report => {
        // Map component based on slug
        let component;
        switch (report.slug) {
          case 'completeness':
            component = CompletenessReport;
            break;
          case 'readiness':
            component = ReadinessReport;
            break;
          case 'velocity':
            component = EnrichmentVelocityReport;
            break;
          case 'localization':
            component = LocalizationCoverageReport;
            break;
          case 'history':
            component = ChangeHistoryReport;
            break;
          case 'attributes':
            component = AttributeInsightsReport;
            break;
          default:
            component = () => <div>{i18n.mainPage.reportNotImplemented}</div>;
        }

        // Get translated name based on slug
        let translatedName = report.name; // fallback to original
        switch (report.slug) {
          case 'completeness':
            translatedName = i18n.getTabsText.dataCompleteness;
            break;
          case 'readiness':
            translatedName = i18n.getTabsText.productReadiness;
            break;
          case 'velocity':
            translatedName = i18n.getTabsText.enrichmentVelocity;
            break;
          case 'localization':
            translatedName = i18n.getTabsText.localizationCoverage;
            break;
          case 'history':
            translatedName = i18n.getTabsText.changeHistory;
            break;
          case 'attributes':
            translatedName = i18n.getTabsText.attributeInsights;
            break;
        }

        return {
          ...report,
          name: translatedName, // Override with translated name
          component,
        };
      }),
    [i18n.mainPage.reportNotImplemented, i18n.getTabsText]
  );

  // Always use the config-defined REPORTS array since it's the source of truth
  const themes = REPORTS.map(r => ({
    slug: r.slug,
    name: r.name,
    description: r.description,
  }));

  if (isLoading) return <div className="flex justify-center p-8">{i18n.mainPage.loading}</div>;

  if (error) return <div className="p-4 text-red-500">{i18n.mainPage.errorLoading}</div>;

  // Find the active report component
  const _ActiveReportComponent =
    REPORTS.find(r => r.slug === activeReport)?.component ||
    (() => <div>{i18n.mainPage.reportNotFound}</div>);

  return (
    <div className="space-y-6 p-2">
      <h1
        className={`text-3xl font-bold ${actualTheme === 'dark' ? 'text-foreground' : 'text-enterprise-900'}`}
      >
        {i18n.mainPage.title}
      </h1>
      <p
        className={`mb-6 ${actualTheme === 'dark' ? 'text-muted-foreground' : 'text-enterprise-600'}`}
      >
        {i18n.mainPage.subtitle}
      </p>
      <Card>
        <CardContent>
          {themes.length > 0 ? (
            <Tabs value={activeReport} onValueChange={setActiveReport} className="w-full">
              <TabsList className="my-4">
                {themes.map(theme => (
                  <TabsTrigger key={theme.slug} value={theme.slug}>
                    {theme.name}
                  </TabsTrigger>
                ))}
              </TabsList>
              {themes.map(theme => {
                const themeReport = REPORTS.find(r => r.slug === theme.slug);
                const ThemeComponent =
                  themeReport?.component || (() => <div>{i18n.mainPage.reportNotFound}</div>);
                return (
                  <TabsContent key={theme.slug} value={theme.slug} className="space-y-4">
                    <div>
                      <ThemeComponent />
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          ) : (
            <div className="rounded-lg border bg-gray-50 p-8 text-center">
              <p className="mb-2 text-gray-600">{i18n.mainPage.noThemesConfigured}</p>
              <p className="text-sm text-gray-500">{i18n.mainPage.noThemesAvailable}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsPage;
